'use client'

import React, { ReactNode, useState, useEffect, useRef, useCallback } from 'react'
import debounce from 'lodash/debounce'
import { cn } from '@scalis/utils'
import { Divider } from '~/scalis-components/core/divider'
import { Option } from '~/scalis-components/core/select/select.types'
import { EmptyList } from '../../../select/components/drop-list/components/empty-list'

function useInfiniteScroll(onLoadMore?: () => Promise<void>) {
  const [isLoading, setIsLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!onLoadMore) return

    const handleScroll = async () => {
      const element = scrollRef.current
      if (!element || isLoading) return

      const { scrollTop, scrollHeight, clientHeight } = element
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100

      if (isNearBottom) {
        setIsLoading(true)
        await onLoadMore()
        setIsLoading(false)
      }
    }

    const element = scrollRef.current
    if (element) {
      element.addEventListener('scroll', handleScroll)
      return () => element.removeEventListener('scroll', handleScroll)
    }
  }, [onLoadMore, isLoading])

  return { scrollRef, isLoading }
}

export interface PopoverProps<T extends Option> {
  options: T[]
  filter: (value: string, option: T) => boolean
  renderItem: (option: T, index: number) => ReactNode
  onLoadMore?: () => Promise<void>
  onSearch?: (searchTerm: string) => Promise<void>
  loading?: boolean
}

export const Popover = <T extends Option>({
  options,
  filter,
  renderItem,
  onLoadMore,
  onSearch,
  loading,
}: PopoverProps<T>) => {
  const [search, setSearch] = useState('')
  const { scrollRef, isLoading } = useInfiniteScroll(onLoadMore)
  // Only filter locally if onSearch is not provided
  const filteredOptions = onSearch ? options : options.filter((option) => filter(search, option))

  // Debounce the search to avoid too many API calls
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      if (onSearch) {
        onSearch(searchTerm)
      }
    }, 300),
    [onSearch],
  )

  // Handle search input changes
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value
    setSearch(newSearchTerm)
    debouncedSearch(newSearchTerm)
  }

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  return (
    <>
      <div className={'flex h-12 items-center gap-2 px-3 py-2 text-neutral-primary'}>
        <i className='fa-regular fa-magnifying-glass' />

        <input
          className={cn(
            'flex h-10 w-full bg-transparent py-3',
            'text-sm leading-6 placeholder:text-neutral-tertiary',
            'outline-hidden disabled:cursor-not-allowed disabled:opacity-50',
          )}
          value={search}
          onChange={handleSearchChange}
          placeholder='Search'
        />
      </div>
      <Divider />
      {loading ? (
        <div className='flex justify-center py-8'>
          <i className='fa-solid fa-spinner fa-spin' />
        </div>
      ) : filteredOptions.length === 0 ? (
        <EmptyList />
      ) : (
        <div
          ref={scrollRef}
          className='max-h-56 overflow-y-auto p-2'
        >
          {filteredOptions.map((option, index) => renderItem(option, index))}
          {isLoading && (
            <div className='flex justify-center py-2'>
              <i className='fa-solid fa-spinner fa-spin' />
            </div>
          )}
        </div>
      )}
    </>
  )
}
